import { LysepAPI } from '../contexts/ApiContext';
import { requestNotificationPermission, getDeviceId, getDeviceName, getPlatform } from './firebase';

/**
 * Register FCM token to backend
 */
export const registerFCMToken = async (fcmToken) => {
  try {
    if (!fcmToken) {
      console.warn('No FCM token provided');
      return { success: false, message: 'No FCM token' };
    }

    const deviceId = getDeviceId();
    const deviceName = getDeviceName();
    const platform = getPlatform();

    const response = await LysepAPI.post('/api/user/device-tokens', {
      deviceId,
      deviceName,
      platform,
      fcmToken,
      pushEnabled: true,
      metadata: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      }
    });

    if (response.data.state) {
      console.log('FCM token registered successfully:', response.data);
      return { success: true, data: response.data.data };
    } else {
      console.error('Failed to register FCM token:', response.data.message);
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    console.error('Error registering FCM token:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to register FCM token'
    };
  }
};

/**
 * Initialize FCM and register token
 */
export const initializeFCM = async () => {
  try {
    // Request notification permission and get token
    const token = await requestNotificationPermission();
    
    if (token) {
      // Register token to backend
      const result = await registerFCMToken(token);
      return result;
    } else {
      return { success: false, message: 'Failed to get FCM token' };
    }
  } catch (error) {
    console.error('Error initializing FCM:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Update/Re-register FCM token to update IP address
 * This is useful when the user revisits the page and IP might have changed
 */
export const updateFCMToken = async () => {
  try {
    const { getFCMToken } = await import('./firebase');
    const fcmToken = await getFCMToken();
    
    if (!fcmToken) {
      console.warn('No FCM token available for update');
      return { success: false, message: 'No FCM token' };
    }

    const deviceId = getDeviceId();
    const deviceName = getDeviceName();
    const platform = getPlatform();

    // Re-register with the same token to update IP address
    const response = await LysepAPI.post('/api/user/device-tokens', {
      deviceId,
      deviceName,
      platform,
      fcmToken,
      pushEnabled: true,
      metadata: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        updatedAt: new Date().toISOString()
      }
    });

    if (response.data.state) {
      console.log('FCM token updated successfully (IP address refreshed):', response.data);
      return { success: true, data: response.data.data };
    } else {
      console.error('Failed to update FCM token:', response.data.message);
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    console.error('Error updating FCM token:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to update FCM token'
    };
  }
};

/**
 * Delete device token from backend
 */
export const deleteDeviceToken = async (tokenId) => {
  try {
    const response = await LysepAPI.delete(`/api/user/device-tokens/${tokenId}`);
    
    if (response.data.state) {
      console.log('Device token deleted successfully');
      return { success: true };
    } else {
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    console.error('Error deleting device token:', error);
    return { success: false, message: error.message };
  }
};

